import { Injectable, inject } from '@angular/core';
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { SimpleAlertComponent } from './simple-alert/simple-alert.component';
import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';
import { NotebookVariablesService } from '../notebook-variables/notebook-variables.service';
import { GeneralHelpers } from '../../helpers/general.helper';

export type SimpleAlertOptions = {
  message: string;
  details?: string;
  okText?: string;
  cancelText?: string;
  defaultInput?: string;
};

export const SimpleAlertOptionsDefaults: SimpleAlertOptions = {
  message: '',
  details: '',
  okText: 'OK',
  cancelText: 'Cancel',
  defaultInput: '',
};

export const ERROR_SHOW_SIMPLE_ALERT = 'Invalid data received for showing alert';
export const ERROR_SHOW_TEXT_PROMPT = 'Invalid data received for showing text prompt';

@Injectable({
  providedIn: 'root'
})
export class SimpleUIService {
  private readonly modalService = inject(BsModalService);
  private readonly notebookVariablesService = inject(NotebookVariablesService);

  modalRef!: BsModalRef;
  currentOptions: SimpleAlertOptions = { message: '' };

  /**
   * Validate input arguments and options for UI dialogs
   * @param args Arguments to validate
   * @param errorConstant Error constant to use for input validation
   * @param context ChunkContext for error handling
   * @returns Validated options
   */
  private validateInputAndGetOptions(
    args: any[], 
    _errorConstant: string, 
    _context?: ChunkContext
  ): SimpleAlertOptions {
    // Handle empty arguments by providing default options
    if (!Array.isArray(args) || args.length === 0) {
      return { ...SimpleAlertOptionsDefaults, message: '' };
    }

    const firstArg = args[0];
    
    // Handle case where first argument is a string (backward compatibility)
    if (typeof firstArg === 'string') {
      return { ...SimpleAlertOptionsDefaults, message: firstArg };
    }
    
    // Handle case where options object doesn't have message
    if (!firstArg || typeof firstArg !== 'object') {
      return { ...SimpleAlertOptionsDefaults, message: '' };
    }
    
    const options = firstArg as SimpleAlertOptions;
    
    // If options object exists but no message, provide empty message for text prompts
    if (!options.message) {
      return { ...SimpleAlertOptionsDefaults, ...options, message: '' };
    }

    return { ...SimpleAlertOptionsDefaults, ...options };
  }

  public async showSimpleAlert(args: any[], context?: ChunkContext): Promise<boolean> {
    try {
      const options = this.validateInputAndGetOptions(args, ERROR_SHOW_SIMPLE_ALERT, context);
      
      return new Promise((resolve) => {
        this.modalRef = this.modalService.show(SimpleAlertComponent, { ignoreBackdropClick: true });
        (<SimpleAlertComponent>this.modalRef.content).options.set(options);
        (<SimpleAlertComponent>this.modalRef.content).exitModal.set((ok?: any) => {
          resolve(ok !== undefined && ok !== null);
          this.modalRef.hide();
          this.modalRef = undefined!;
        });
      });
    } catch (error: any) {
      console.error('Error showing alert:', error);
      if (context) {
        context.addMessage(`Error showing alert: ${error.message}`, 'danger');
        context.setChunkResultStatus("error");
      }
      throw error; // Rethrow to allow try/catch blocks to work
    }
  }

  public async showTextPrompt(args: any[], context?: ChunkContext): Promise<string | undefined> {
    try {
      const options = this.validateInputAndGetOptions(args, ERROR_SHOW_TEXT_PROMPT, context);
      
      return new Promise((resolve) => {
        this.modalRef = this.modalService.show(SimpleAlertComponent, { ignoreBackdropClick: true });
        (<SimpleAlertComponent>this.modalRef.content).options.set(options);
        (<SimpleAlertComponent>this.modalRef.content).promptForInput.set(true);
        (<SimpleAlertComponent>this.modalRef.content).exitModal.set((ok?: string) => {
          resolve(ok);
          this.modalRef.hide();
          this.modalRef = undefined!;
        });
      });
    } catch (error: any) {
      console.error('Error showing text prompt:', error);
      if (context) {
        context.addMessage(`Error showing text prompt: ${error.message}`, 'danger');
        context.setChunkResultStatus("error");
      }
      throw error; // Rethrow to allow try/catch blocks to work
    }
  }

  /**
   * Get job arguments (webhook payload).
   * In enoch/headless, returns the webhook payload for the current job.
   * In ehq-web, returns the notebook global variable `job_arguments` when set
   * (for local step testing); otherwise an empty object.
   */
  public getJobArguments(_args?: any[], _context?: ChunkContext): Record<string, any> {
    const list =
      typeof this.notebookVariablesService.variableList$ === 'function'
        ? this.notebookVariablesService.variableList$()
        : this.notebookVariablesService.variableList;
    return SimpleUIService.resolveJobArgumentsFromVariables(list);
  }

  /**
   * Parse mock webhook/job args from the `job_arguments` notebook global variable.
   * Used when running webhook-triggered workflows via "Run All Steps" in the browser.
   */
  static resolveJobArgumentsFromVariables(
    variableList: Array<{ name?: string; value?: unknown }> | null | undefined,
  ): Record<string, any> {
    const entry = variableList?.find(v =>
      SimpleUIService.isJobArgumentsVariableName(v.name),
    );
    if (entry?.value == null || entry.value === '') {
      return {};
    }

    const parsed = SimpleUIService.parseJobArgumentsValue(entry.value);
    if (Object.keys(parsed).length === 0) {
      console.warn(
        '[getJobArguments] A "job_arguments" workflow variable exists but its value is not a JSON object. ' +
          'Paste the contents of scripts/gcg-job-args.json into the variable value field.',
      );
    }
    return parsed;
  }

  /** True when the workflow variable name is the mock webhook payload slot. */
  static isJobArgumentsVariableName(name?: string): boolean {
    const normalized = (name ?? '').trim().toLowerCase();
    return normalized === 'job_arguments' || normalized === 'jobarguments';
  }

  /** Normalize a workflow variable value into a job-arguments object. */
  static parseJobArgumentsValue(raw: unknown): Record<string, any> {
    if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
      return raw as Record<string, any>;
    }

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) {
        return {};
      }
      if (GeneralHelpers.isJSON(trimmed)) {
        const parsed = GeneralHelpers.jsonParse<Record<string, any>>(trimmed);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return parsed;
        }
      }
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return parsed as Record<string, any>;
        }
      } catch {
        // not JSON — ignore
      }
    }

    return {};
  }

}
